import type { AxiosInstance } from 'axios';
import type { paths, components } from '../schema';
import type { TimelineEntry } from '../types/timeline';
import type {
  CursorPaginationResponse,
  RequestOptions,
  RequestOptionsWithParams,
} from './types';
import { curry1 } from './utils';

type CreateAutomationResponse =
  paths['/api/automation2/automations/{automation_identifier}']['get']['responses']['200']['content']['application/json'];

type CreateAutomationFolderResponse =
  paths['/api/automation2/folders']['post']['responses']['201']['content']['application/json'];

export type GetAutomationResponse =
  paths['/api/automation2/automations/{automation_identifier}']['get']['responses']['200']['content']['application/json'];

export type Automation = Mutable<
  paths['/api/automation2/automations/{automation_identifier}']['get']['responses']['200']['content']['application/json']
>;

type GetAutomationFoldersResponse = Mutable<
  paths['/api/automation2/folders']['get']['responses']['200']['content']['application/json']
>;

export type AutomationFolder = GetAutomationFoldersResponse[number];

type GetAutomationSubfoldersResponse =
  paths['/api/automation2/folders/{id}/subfolders']['get']['responses']['200']['content']['application/json'];

type PatchAutomationResponse =
  paths['/api/automation2/automations/{automation_identifier}']['patch']['responses']['200']['content']['application/json'];

type PatchAutomationFolderResponse =
  paths['/api/automation2/folders/{id}']['patch']['responses']['200']['content']['application/json'];

type GetAutomationMetaDataResponse =
  paths['/api/automation2/automations/metadata']['get']['responses']['200']['content']['application/json'];

export type RunToolsAsOptionsParams = Required<
  paths['/api/automation2/automations/run-tools-as-options']['get']['parameters']
>['query'];

export type RunToolsAsOptions =
  paths['/api/automation2/automations/run-tools-as-options']['get']['responses']['200']['content']['application/json'];

type PostAutomationExecutionSkipAndResumeRequestBody =
  paths['/api/automation2/automation-execution/{id}/skip-and-resume']['post']['requestBody']['content']['application/json'];

type PostAutomationDebugStartResponse =
  paths['/api/automation2/automations/{automation_identifier}/debug-start']['post']['responses']['200']['content']['application/json'];

type PostAutomationExecutionDebugStepResponse =
  paths['/api/automation2/automation-execution/{id}/debug-step']['post']['responses']['200']['content']['application/json'] & {
    started_executions: Record<string, string[]>;
    restarted_executions: Record<string, string[]>;
  };

export type AutomationExecutionParams = Required<
  paths['/api/automation2/automation-execution']['get']['parameters']
>['query'];

export type AutomationExecutionStatus =
  components['schemas']['ReadEntityAutomationExecutionStatusEnum'];

export type AutomationExecutionStepStatus =
  components['schemas']['AutomationHistoryStatusEnum'];

type AutomationExecutionResponse =
  paths['/api/automation2/automation-execution/{id}']['get']['responses']['200']['content']['application/json'];

export type AutomationExecution = Omit<
  AutomationExecutionResponse,
  'status' | 'automation'
> & {
  status: AutomationExecutionStatus;
  start_time: string;
  trigger: {
    id: string;
    type: 'manual' | string;
    deleted: boolean;
  };
  automation: AutomationExecutionResponse['automation'] & {
    custom_object_id?: string | null;
  };
};

export type AutomationHistoryParams = Required<
  paths['/api/automation2/automations/{automation_identifier}/modification-history']['get']['parameters']
>['query'];

export type CreateAutomationMessageResponse =
  paths['/api/messages/automations/automation/{automation_id}']['post']['responses']['201']['content']['application/json'];

export type GetAutomationMessageResponse =
  paths['/api/messages/automations/{id}']['get']['responses']['200']['content']['application/json'];

export type PatchAutomationMessageResponse =
  paths['/api/messages/automations/{id}']['patch']['responses']['200']['content']['application/json'];

export type PostAutomationDebugRerunResponse =
  paths['/api/automation2/automation-execution/{id}/debug-rerun']['post']['responses']['200']['content']['application/json'];

export type PostAutomationDebugRestartResponse =
  paths['/api/automation2/automation-execution/{id}/debug-restart']['post']['responses']['200']['content']['application/json'];

export type GetAutomationMessageParams = Required<
  paths['/api/messages/automations/{id}']['get']['parameters']
>['query'];

export type AutomationTimelineEntry = TimelineEntry<
  Record<string, any>,
  | 'automation_created'
  | 'automation_updated'
  | 'automation_deleted'
  | 'automation_started'
  | 'automation_paused'
  | 'automation_cancelled'
  | 'automation_resumed'
>;

export type AutomationHistory = Omit<
  paths['/api/automation2/automations/{automation_identifier}/modification-history']['get']['responses']['200']['content']['application/json'],
  'results'
> & {
  results: AutomationTimelineEntry[];
};

type PersistedBoolean = {
  data_type: 'boolean';
  data_subtype: string | null;
  is_array: boolean;
  value: {
    raw_value: string;
    display_value: string;
  };
};

type PersistedDate = {
  data_type: 'date';
  data_subtype: string | null;
  is_array: boolean;
  value: {
    raw_value: string;
    display_value: string;
  };
};

type PersistedDateTime = {
  data_type: 'datetime';
  data_subtype: string | null;
  is_array: boolean;
  value: {
    raw_value: string;
    display_value: string;
  };
};

type PersistedEmployee = {
  data_type: 'employee';
  data_subtype: string | null;
  is_array: boolean;
  value: {
    raw_value: string;
    display_value: string;
  };
};

type PersistedEntity = {
  data_type: 'entity';
  data_subtype: string | null;
  is_array: boolean;
  value: {
    raw_value: string;
    display_value:
      | {
          id: string;
          first_name: string;
          last_name: string;
          email: string;
          display_name: string;
          created: string;
          updated: string;
          deleted: boolean;
        }
      | {
          id: string;
          name: string;
          display_name: string;
          created: string;
          updated: string;
          deleted: boolean;
        };
  };
};

type PersistedNumber = {
  data_type: 'number';
  data_subtype: string | null;
  is_array: boolean;
  value: {
    raw_value: string;
    display_value: string;
  };
};

type PersistedPhoneNumber = {
  data_type: 'phone_number';
  data_subtype: string | null;
  is_array: boolean;
  value: {
    raw_value: string;
    display_value: string;
  };
};

type PersistedString = {
  data_type: 'string';
  data_subtype: string | null;
  is_array: boolean;
  value: {
    raw_value: string;
    display_value: string;
  };
};

type PersistedUUID = {
  data_type: 'uuid';
  data_subtype: string | null;
  is_array: boolean;
  value: {
    raw_value: string;
    display_value: string;
  };
};

type PersistedValue =
  | PersistedBoolean
  | PersistedDate
  | PersistedDateTime
  | PersistedEmployee
  | PersistedEntity
  | PersistedNumber
  | PersistedPhoneNumber
  | PersistedString
  | PersistedUUID;

export type AutomationExecutionVariable = {
  persisted_value: PersistedValue;
  variable: {
    id: string;
    name: string;
    data_type: PersistedValue['data_type'];
    data_subtype: string | null;
    deleted: boolean;
  };
};

export type AutomationExecutionVariables = Record<
  string,
  AutomationExecutionVariable
>;

export const createAutomation = async (
  instance: AxiosInstance,
  data: paths['/api/automation2/automations']['post']['requestBody']['content']['application/json'],
  options?: RequestOptions
): Promise<CreateAutomationResponse> => {
  const res = await instance.post<CreateAutomationResponse>(
    '/automation2/automations',
    data,
    options
  );

  return res.data;
};

export const createAutomationFolder = async (
  instance: AxiosInstance,
  data: paths['/api/automation2/folders']['post']['requestBody']['content']['application/json'],
  options?: RequestOptions
): Promise<CreateAutomationFolderResponse> => {
  const res = await instance.post<CreateAutomationFolderResponse>(
    '/automation2/folders',
    data,
    options
  );

  return res.data;
};

export const deleteAutomationFolder = async (
  instance: AxiosInstance,
  id: string,
  options?: RequestOptionsWithParams<
    paths['/api/automation2/folders/{id}']['delete']['parameters']['query']
  >
): Promise<void> => {
  await instance.delete(`/automation2/folders/${id}`, options);
};

export const getAutomation = async (
  instance: AxiosInstance,
  id: string,
  options?: RequestOptions
): Promise<Automation> => {
  const res = await instance.get<Automation>(
    `/automation2/automations/${id}`,
    options
  );

  return res.data;
};

export const getAutomationFolders = async (
  instance: AxiosInstance,
  options?: RequestOptions
): Promise<GetAutomationFoldersResponse> => {
  const res = await instance.get<GetAutomationFoldersResponse>(
    '/automation2/folders',
    options
  );

  return res.data;
};

export const getAutomationHistory = async (
  instance: AxiosInstance,
  id: string,
  options?: RequestOptionsWithParams<AutomationHistoryParams>
): Promise<AutomationHistory> => {
  const res = await instance.get<AutomationHistory>(
    `/automation2/automations/${id}/modification-history`,
    options
  );

  return res.data;
};

export const getAutomationSubfolders = async (
  instance: AxiosInstance,
  id: string,
  options?: RequestOptionsWithParams<
    paths['/api/automation2/folders/{id}/subfolders']['get']['parameters']['query']
  >
): Promise<GetAutomationSubfoldersResponse> => {
  const res = await instance.get<GetAutomationSubfoldersResponse>(
    `/automation2/folders/${id}/subfolders`,
    options
  );

  return res.data;
};

export const patchAutomation = async (
  instance: AxiosInstance,
  id: string,
  data: Partial<
    Required<
      paths['/api/automation2/automations/{automation_identifier}']['patch']
    >['requestBody']['content']['application/json']
  >,
  options?: RequestOptions
): Promise<PatchAutomationResponse> => {
  const res = await instance.patch<PatchAutomationResponse>(
    `/automation2/automations/${id}`,
    data,
    options
  );

  return res.data;
};

export const patchAutomationFolder = async (
  instance: AxiosInstance,
  id: string,
  data: Required<
    paths['/api/automation2/folders/{id}']['patch']
  >['requestBody']['content']['application/json'],
  options?: RequestOptions
): Promise<PatchAutomationFolderResponse> => {
  const res = await instance.patch<PatchAutomationFolderResponse>(
    `/automation2/folders/${id}`,
    data,
    options
  );

  return res.data;
};

export const getAutomationExecutionVariables = async (
  instance: AxiosInstance,
  id: string,
  options?: RequestOptions
): Promise<AutomationExecutionVariables> => {
  const res = await instance.get<AutomationExecutionVariables>(
    `/automation2/automation-execution/${id}/variables`,
    options
  );

  return res.data;
};

export const getAutomationMetaData = async (
  instance: AxiosInstance,
  options?: RequestOptions
): Promise<GetAutomationMetaDataResponse> => {
  const res = await instance.get<GetAutomationMetaDataResponse>(
    `/automation2/automations/metadata`,
    options
  );

  return res.data;
};

export const getRunToolsAsOptions = async (
  instance: AxiosInstance,
  options?: RequestOptionsWithParams<RunToolsAsOptionsParams>
): Promise<RunToolsAsOptions> => {
  const res = await instance.get<RunToolsAsOptions>(
    `/automation2/automations/run-tools-as-options`,
    options
  );

  return res.data;
};

export const postAutomationExecutionPlay = async (
  instance: AxiosInstance,
  id: string,
  options?: RequestOptions
): Promise<void> => {
  await instance.post(
    `/automation2/automation-execution/${id}/play`,
    {},
    options
  );
};

export const postAutomationExecutionSkipAndResume = async (
  instance: AxiosInstance,
  id: string,
  body: PostAutomationExecutionSkipAndResumeRequestBody,
  options?: RequestOptions
): Promise<void> => {
  await instance.post(
    `/automation2/automation-execution/${id}/skip-and-resume`,
    body,
    options
  );
};

export const postAutomationDebugStart = async (
  instance: AxiosInstance,
  id: string,
  data: Required<
    paths['/api/automation2/automations/{automation_identifier}/debug-start']['post']
  >['requestBody']['content']['application/json'],
  options?: RequestOptions
): Promise<PostAutomationDebugStartResponse> => {
  const res = await instance.post<PostAutomationDebugStartResponse>(
    `/automation2/automations/${id}/debug-start`,
    data,
    options
  );

  return res.data;
};

export const postAutomationExecutionCancel = async (
  instance: AxiosInstance,
  id: string
): Promise<void> => {
  await instance.post(`/automation2/automation-execution/${id}/cancel`);
};

export const postAutomationExecutionDebugStep = async (
  instance: AxiosInstance,
  id: string,
  data: Required<
    paths['/api/automation2/automation-execution/{id}/debug-step']['post']
  >['requestBody']['content']['application/json']
): Promise<PostAutomationExecutionDebugStepResponse> => {
  const res = await instance.post<PostAutomationExecutionDebugStepResponse>(
    `/automation2/automation-execution/${id}/debug-step`,
    data
  );

  return res.data;
};

export const postAutomationExecutionDebugSendit = async (
  instance: AxiosInstance,
  id: string
): Promise<void> => {
  await instance.post(`/automation2/automation-execution/${id}/debug-sendit`);
};

export const postAutomationExecutionPause = async (
  instance: AxiosInstance,
  id: string
): Promise<void> => {
  await instance.post(`/automation2/automation-execution/${id}/pause`);
};

export const createAutomationMessage = async (
  instance: AxiosInstance,
  automation_id: string,
  data: paths['/api/messages/automations/automation/{automation_id}']['post']['requestBody']['content']['application/json'],
  options?: RequestOptions
): Promise<CreateAutomationMessageResponse> => {
  const res = await instance.post<CreateAutomationMessageResponse>(
    `/messages/automations/automation/${automation_id}`,
    data,
    options
  );

  return res.data;
};

export const getAutomationExecution = async (
  instance: AxiosInstance,
  options?: RequestOptionsWithParams<AutomationExecutionParams>
): Promise<CursorPaginationResponse<AutomationExecution>> => {
  const res = await instance.get<CursorPaginationResponse<AutomationExecution>>(
    `/automation2/automation-execution`,
    options
  );

  return res.data;
};

export const getAutomationExecutionById = async (
  instance: AxiosInstance,
  id: string
): Promise<AutomationExecution> => {
  const res = await instance.get<AutomationExecution>(
    `/automation2/automation-execution/${id}`
  );

  return res.data;
};

export const getAutomationMessage = async (
  instance: AxiosInstance,
  id: string,
  options?: RequestOptions
): Promise<GetAutomationMessageResponse> => {
  const res = await instance.get<GetAutomationMessageResponse>(
    `/messages/automations/${id}`,
    options
  );

  return res.data;
};

export const patchAutomationMessage = async (
  instance: AxiosInstance,
  id: string,
  data: Required<
    paths['/api/messages/automations/{id}']['patch']
  >['requestBody']['content']['application/json'],
  options?: RequestOptions
): Promise<PatchAutomationMessageResponse> => {
  const res = await instance.patch<PatchAutomationMessageResponse>(
    `/messages/automations/${id}`,
    data,
    options
  );
  return res.data;
};

export const postAutomationDebugRerun = async (
  instance: AxiosInstance,
  id: string,
  data: Required<
    paths['/api/automation2/automation-execution/{id}/debug-rerun']['post']
  >['requestBody']['content']['application/json'],
  options?: RequestOptions
): Promise<PostAutomationDebugRerunResponse> => {
  const res = await instance.post<PostAutomationDebugRerunResponse>(
    `/automation2/automation-execution/${id}/debug-rerun`,
    data,
    options
  );
  return res.data;
};

export const postAutomationDebugRestart = async (
  instance: AxiosInstance,
  id: string,
  data: Required<
    paths['/api/automation2/automation-execution/{id}/debug-restart']['post']
  >['requestBody']['content']['application/json'],
  options?: RequestOptions
): Promise<PostAutomationDebugRestartResponse> => {
  const res = await instance.post<PostAutomationDebugRestartResponse>(
    `/automation2/automation-execution/${id}/debug-restart`,
    data,
    options
  );
  return res.data;
};

export class AutomationService {
  public createAutomation;
  public createAutomationFolder;
  public deleteAutomationFolder;
  public getAutomation;
  public getAutomationSubfolders;
  public patchAutomationFolder;
  public getAutomationHistory;
  public getAutomationExecutionVariables;
  public getAutomationMetaData;
  public getRunToolsAsOptions;
  public postAutomationExecutionPlay;
  public postAutomationExecutionSkipAndResume;
  public postAutomationDebugStart;
  public postAutomationExecutionCancel;
  public postAutomationExecutionDebugStep;
  public postAutomationExecutionDebugSendit;
  public postAutomationExecutionPause;
  public createAutomationMessage;
  public getAutomationMessage;
  public patchAutomationMessage;
  public getAutomationExecution;
  public postAutomationDebugRerun;
  public postAutomationDebugRestart;

  constructor(private instance: AxiosInstance) {
    this.createAutomation = curry1(createAutomation)(this.instance);
    this.createAutomationFolder = curry1(createAutomationFolder)(this.instance);
    this.deleteAutomationFolder = curry1(deleteAutomationFolder)(this.instance);
    this.getAutomation = curry1(getAutomation)(this.instance);
    this.getAutomationSubfolders = curry1(getAutomationSubfolders)(
      this.instance
    );
    this.patchAutomationFolder = curry1(patchAutomationFolder)(this.instance);
    this.getAutomationHistory = curry1(getAutomationHistory)(this.instance);
    this.getAutomationExecutionVariables = curry1(
      getAutomationExecutionVariables
    )(this.instance);
    this.getAutomationMetaData = curry1(getAutomationMetaData)(this.instance);
    this.getRunToolsAsOptions = curry1(getRunToolsAsOptions)(this.instance);
    this.postAutomationExecutionPlay = curry1(postAutomationExecutionPlay)(
      this.instance
    );
    this.postAutomationExecutionSkipAndResume = curry1(
      postAutomationExecutionSkipAndResume
    )(this.instance);
    this.postAutomationDebugStart = curry1(postAutomationDebugStart)(
      this.instance
    );
    this.postAutomationExecutionCancel = curry1(postAutomationExecutionCancel)(
      this.instance
    );
    this.postAutomationExecutionDebugStep = curry1(
      postAutomationExecutionDebugStep
    )(this.instance);
    this.postAutomationExecutionDebugSendit = curry1(
      postAutomationExecutionDebugSendit
    )(this.instance);
    this.postAutomationExecutionPause = curry1(postAutomationExecutionPause)(
      this.instance
    );
    this.createAutomationMessage = curry1(createAutomationMessage)(
      this.instance
    );
    this.getAutomationMessage = curry1(getAutomationMessage)(this.instance);
    this.patchAutomationMessage = curry1(patchAutomationMessage)(this.instance);
    this.getAutomationExecution = curry1(getAutomationExecution)(this.instance);
    this.postAutomationDebugRerun = curry1(postAutomationDebugRerun)(
      this.instance
    );
    this.postAutomationDebugRestart = curry1(postAutomationDebugRestart)(
      this.instance
    );
  }
}
